// ADDENDUM 44 (E50) — la barra de «tiempo vs teórico», en la lista de Workflow y en el desplegable.
//
// ⚠️⚠️ ESTO ERA UN RATIO SATURADO Y AHORA SON DOS TRAMOS APILADOS. El cambio lo trajo el segundo
// mockup del PO y merece explicarse, porque resuelve un problema que yo había tapado en vez de
// resuelto:
//
//   EL 44% DE LOS TIEMPOS TEÓRICOS POSITIVOS VALE EXACTAMENTE 1 SEGUNDO — 1.487 tareas de 3.370,
//   casi con seguridad un valor por defecto de importación.
//
// Con un ratio `real / teórico`, ese 44% da anchos de seis cifras y hay que SATURARLOS al 100%. La
// barra deja entonces de medir: todas esas filas se ven iguales, llenas, y el ancho no distingue
// un exceso de un segundo de uno de cinco horas.
//
// Apilando NO HAY DIVISOR QUE EXPLOTE, porque los dos tramos son magnitudes reales y su suma es el
// tiempo real:
//
//     teórico 3s ┃▓▓░░░░░░░░░░░░░░░░░░░░░░░░░░░░┃ desviación 1m 04s      total = real 1m 07s
//
// El teórico de 1 segundo se ve por lo que es —un hilo diminuto al lado de una desviación enorme—
// sin umbrales, sin colores que decidan por el usuario y sin esconder nada. Es exactamente lo que
// RN8 pide (lo sospechoso se enseña, no se filtra) y lo que el ratio saturado impedía ver.
//
// 📌 EL TOTAL ES `max(real, teórico)`, no el real. Dentro del teórico, el tramo verde es lo
// consumido y lo que queda es pista vacía: así la misma barra sirve para "voy por la mitad" y para
// "me he pasado", y las dos se leen sin cambiar de escala mental.
// 📌 EL DELTA SALE DE `timeDeviation` Y NO SE RECALCULA AQUÍ. Es una resta de una línea y podría
// escribirse en los dos sitios; escribirla dos veces es como acaban discrepando dos vistas del
// mismo hecho. Allí vive además el porqué de que la desviación sea una MAGNITUD CON SIGNO y no un
// porcentaje, que es la decisión que esta barra hereda.
import { timeDeviation } from './timeDeviation'
import { asNumber } from './detailShared'

/**
 * @param {number|string|null} theoreticalSeconds el teórico tal cual lo sirve N3/N4 (`''` si falta)
 * @param {number|string|null} actualSeconds el real tal cual lo sirve N3/N4 (`''` si falta)
 * @returns {{computable: boolean, over: boolean, deltaSeconds: number,
 *            theoreticalFraction: number, deviationFraction: number}}
 *   Las dos fracciones van en [0, 1] y son ANCHOS de tramo, no medidas. `computable: false` NO es
 *   cero y no se puede pintar como "a tiempo": es que falta un sumando.
 */
export const timeVsTheoretical = (theoreticalSeconds, actualSeconds) => {
  const theoretical = asNumber(theoreticalSeconds)
  const actual = asNumber(actualSeconds)
  // Falta cualquiera de los dos: no hay resta que hacer. Es la única condición, y es aritmética —
  // no un juicio sobre si el número que hay merece confianza.
  if (theoretical === null || actual === null) {
    return { computable: false, over: false, deltaSeconds: 0, theoreticalFraction: 0, deviationFraction: 0 }
  }

  const { deltaSeconds } = timeDeviation(theoreticalSeconds, actualSeconds)
  const over = deltaSeconds > 0
  const total = Math.max(actual, theoretical, 0)

  // ⚠️ TOTAL CERO NO ES UNA BARRA VACÍA POR ERROR: es que no hay nada que repartir. Sin esta rama
  // sale `0/0 = NaN`, y un `width: NaN%` no se pinta — la barra DESAPARECERÍA en silencio en vez
  // de salir a cero, que es un fallo que se lee como "esta fila no tiene datos" teniéndolos.
  if (total <= 0) {
    return { computable: true, over, deltaSeconds, theoreticalFraction: 0, deviationFraction: 0 }
  }

  // Dentro del teórico, el tramo verde es LO CONSUMIDO (el real), no el teórico entero: pintar el
  // teórico completo diría que ya se ha gastado todo el margen. Pasado el teórico, el verde es el
  // teórico y el ámbar el exceso, y los dos juntos suman el real.
  const theoreticalWidth = over ? theoretical : actual
  return {
    computable: true,
    over,
    deltaSeconds,
    theoreticalFraction: Math.min(1, Math.max(0, theoreticalWidth / total)),
    deviationFraction: over ? Math.min(1, Math.max(0, deltaSeconds / total)) : 0
  }
}
